import { WorkflowExecutionResult } from '@/components/Workflow/Preview/WorkflowFormProvider'
import LocalStorageManager from '@/lib/utils/localStorageManager'
import { useCallback, useEffect, useMemo, useReducer, useRef } from 'react'
import { NodeExecution, WorkflowRefStrict } from 'sprites-types/api/v2/workflows'
import { workflowStateStorage } from './workflowStateStorage'
import {
  UnifiedWorkflowState,
  WorkflowBuildResult,
  WorkflowPhase,
  WorkflowStateEvent,
  WorkflowStateManager,
  WorkflowStateManagerOptions,
  initialWorkflowState,
} from './workflowStateTypes'

/**
 * Reducer function for workflow state management
 */
function workflowStateReducer(
  state: UnifiedWorkflowState,
  event: WorkflowStateEvent
): UnifiedWorkflowState {
  switch (event.type) {
    case 'PHASE_CHANGE':
      return {
        ...state,
        phase: event.payload.phase,
      }

    case 'BUILD_START':
      return {
        ...state,
        phase: 'building',
        isBuilding: true,
        buildState: {
          isBuilding: true,
          buildProgress: 0,
          buildMessage: event.payload.message || 'Starting build...',
        },
        progress: {
          percentage: 0,
          message: event.payload.message || 'Starting build...',
        },
        error: undefined,
      }

    case 'BUILD_PROGRESS':
      return {
        ...state,
        buildState: {
          ...state.buildState,
          buildProgress: event.payload.progress,
          buildMessage: event.payload.message || state.buildState.buildMessage,
        },
        progress: {
          percentage: event.payload.progress,
          message: event.payload.message || state.progress.message,
        },
      }

    case 'BUILD_COMPLETE':
      return {
        ...state,
        phase: event.payload.result.success ? 'idle' : 'error',
        isBuilding: false,
        buildState: {
          isBuilding: false,
          buildProgress: 100,
          buildMessage: 'Build completed',
          buildError: event.payload.result.error,
        },
        buildResult: event.payload.result,
        progress: {
          percentage: 100,
          message: event.payload.result.success ? 'Build completed successfully' : 'Build failed',
        },
        error: event.payload.result.error,
      }

    case 'BUILD_ERROR':
      return {
        ...state,
        phase: 'error',
        isBuilding: false,
        buildState: {
          isBuilding: false,
          buildProgress: 0,
          buildMessage: 'Build failed',
          buildError: event.payload.error,
        },
        error: event.payload.error,
        lastError: {
          phase: 'building',
          message: event.payload.error,
          timestamp: Date.now(),
        },
      }

    case 'EXECUTION_START':
      return {
        ...state,
        phase: 'executing',
        isExecuting: true,
        workflowRef: event.payload.workflowRef,
        executionState: {
          isExecuting: true,
          executionProgress: 0,
          executionMessage: event.payload.message || 'Starting workflow execution...',
        },
        progress: {
          percentage: 0,
          message: event.payload.message || 'Starting workflow execution...',
        },
        error: undefined,
        // Don't clear executionResult here - preserve previous successful results during execution
      }

    case 'EXECUTION_PROGRESS':
      return {
        ...state,
        executionState: {
          ...state.executionState,
          executionProgress: event.payload.progress,
          executionMessage: event.payload.message || state.executionState.executionMessage,
        },
        progress: {
          percentage: event.payload.progress,
          message: event.payload.message || state.progress.message,
        },
      }

    case 'EXECUTION_COMPLETE':
      return {
        ...state,
        phase: 'completed',
        isExecuting: false,
        executionState: {
          isExecuting: false,
          executionProgress: 100,
          executionMessage: 'Workflow completed',
        },
        executionResult: event.payload.result,
        progress: {
          percentage: 100,
          message: 'Workflow completed successfully',
        },
      }

    case 'EXECUTION_ERROR':
      return {
        ...state,
        phase: 'error',
        isExecuting: false,
        executionState: {
          isExecuting: false,
          executionProgress: 0,
          executionMessage: 'Workflow failed',
          executionError: event.payload.error,
        },
        error: event.payload.error,
        lastError: {
          phase: 'executing',
          message: event.payload.error,
          timestamp: Date.now(),
        },
      }

    case 'CONTEXT_UPDATE':
      return {
        ...state,
        ...event.payload,
      }

    case 'NODE_EXECUTION_UPDATE':
      return {
        ...state,
        nodeExecutions: {
          ...state.nodeExecutions,
          [event.payload.executionId]: event.payload.execution,
        },
      }

    case 'RESET':
      return {
        ...initialWorkflowState,
      }

    default:
      return state
  }
}

/**
 * Custom hook for managing unified workflow state
 */
export function useWorkflowStateManager(
  options: WorkflowStateManagerOptions = {}
): WorkflowStateManager {
  const {
    debug = false,
    onStateChange,
    onPhaseChange,
    onError,
    persistState = false,
    storageKey = 'workflow-state',
  } = options

  // Synchronously load workflow state (small) from localStorage
  // nodeExecutions (large) will be loaded async from IndexedDB
  const getInitialState = useCallback((): UnifiedWorkflowState => {
    if (!persistState || typeof window === 'undefined') {
      return initialWorkflowState
    }

    try {
      const stored = LocalStorageManager.getItem(storageKey)
      if (stored) {
        const parsedState = JSON.parse(stored)
        if (parsedState && typeof parsedState.phase === 'string') {
          // Return state without nodeExecutions - those load async from IndexedDB
          // Initialize with empty object to prevent showing nodes as pending during hydration
          const { nodeExecutions: _, ...stateWithoutExecutions } = parsedState
          return { ...initialWorkflowState, ...stateWithoutExecutions, nodeExecutions: {} }
        }
      }
    } catch (error) {
      console.warn('[WorkflowStateManager] Failed to load workflow state from localStorage:', error)
    }

    return initialWorkflowState
  }, [persistState, storageKey])

  const [state, dispatch] = useReducer(workflowStateReducer, undefined, getInitialState)
  const previousPhaseRef = useRef<WorkflowPhase>(state.phase)
  const isHydratedRef = useRef(false)
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  // Load nodeExecutions from IndexedDB (async, after initial render)
  // Workflow state is already loaded synchronously from localStorage
  useEffect(() => {
    if (!persistState || typeof window === 'undefined' || isHydratedRef.current) {
      isHydratedRef.current = true
      return
    }

    let cancelled = false

    // Load large nodeExecutions data from IndexedDB
    workflowStateStorage
      .loadState(storageKey)
      .then((loadedState) => {
        if (cancelled) return

        // Only load nodeExecutions from IndexedDB (rest is in localStorage)
        if (loadedState?.nodeExecutions) {
          dispatch({
            type: 'CONTEXT_UPDATE',
            payload: { nodeExecutions: loadedState.nodeExecutions },
          })
        }
        isHydratedRef.current = true
      })
      .catch((error) => {
        if (cancelled) return
        console.warn('[WorkflowStateManager] Failed to load nodeExecutions from IndexedDB:', error)
        isHydratedRef.current = true
      })

    return () => {
      cancelled = true
    }
  }, [persistState, storageKey])

  // Persist state: workflow metadata to localStorage, nodeExecutions to IndexedDB
  useEffect(() => {
    if (!persistState || typeof window === 'undefined' || !isHydratedRef.current) {
      return
    }

    // Clear pending save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current)
    }

    // Debounce saves to avoid excessive writes
    saveTimeoutRef.current = setTimeout(() => {
      // Save workflow state (without executions) to localStorage for sync access
      try {
        const { nodeExecutions: _, ...stateWithoutExecutions } = state
        const success = LocalStorageManager.setItem(
          storageKey,
          JSON.stringify(stateWithoutExecutions)
        )
        if (!success) {
          console.error(
            '[WorkflowStateManager] Failed to save workflow state to localStorage - quota exceeded or other error'
          )
        }
      } catch (error) {
        console.error(
          '[WorkflowStateManager] Failed to save workflow state to localStorage:',
          error
        )
      }

      // Save nodeExecutions to IndexedDB (handles large data)
      workflowStateStorage
        .saveState(storageKey, state)
        .then(() => workflowStateStorage.cleanupOldStates(storageKey, 5))
        .catch((error) => {
          console.error('[WorkflowStateManager] Failed to save nodeExecutions to IndexedDB:', error)
        })
    }, 300)

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current)
      }
    }
  }, [state, persistState, storageKey])

  // Handle phase change callbacks (most specific - runs before state change)
  useEffect(() => {
    if (state.phase === previousPhaseRef.current) return

    const previous = previousPhaseRef.current
    previousPhaseRef.current = state.phase

    if (debug) {
      console.log('[WorkflowStateManager] Phase changed:', previous, '->', state.phase)
    }

    onPhaseChange?.(state.phase, previous)
  }, [state.phase, onPhaseChange, debug])

  // Handle error callbacks (specific to error state)
  const lastErrorRef = useRef<{ message: string; phase: WorkflowPhase } | null>(null)
  useEffect(() => {
    if (state.phase !== 'error' || !state.error) {
      lastErrorRef.current = null
      return
    }

    const currentError = { message: state.error, phase: state.lastError?.phase || state.phase }
    const isNewError =
      !lastErrorRef.current ||
      lastErrorRef.current.message !== currentError.message ||
      lastErrorRef.current.phase !== currentError.phase

    if (isNewError) {
      if (debug) {
        console.error('[WorkflowStateManager] Error occurred:', state.error)
      }
      onError?.(state.error, currentError.phase)
      lastErrorRef.current = currentError
    }
  }, [state.phase, state.error, state.lastError?.phase, onError, debug])

  // Handle state change callbacks (general - runs after phase change effect)
  useEffect(() => {
    if (debug) {
      console.log('[WorkflowStateManager] State changed:', state)
    }

    onStateChange?.(state, {
      type: 'PHASE_CHANGE',
      payload: { phase: state.phase },
    })
  }, [state, onStateChange, debug])

  // Action creators (stable - dispatch never changes in useReducer)
  const setPhase = useCallback((phase: WorkflowPhase) => {
    dispatch({ type: 'PHASE_CHANGE', payload: { phase } })
  }, [])

  const resetState = useCallback(() => {
    dispatch({ type: 'RESET' })
  }, [])

  const startBuilding = useCallback((message?: string) => {
    dispatch({ type: 'BUILD_START', payload: { message } })
  }, [])

  const updateBuildProgress = useCallback((progress: number, message?: string) => {
    dispatch({ type: 'BUILD_PROGRESS', payload: { progress, message } })
  }, [])

  const completeBuild = useCallback((buildResult: WorkflowBuildResult) => {
    dispatch({ type: 'BUILD_COMPLETE', payload: { result: buildResult } })
  }, [])

  const errorBuild = useCallback((error: string) => {
    dispatch({ type: 'BUILD_ERROR', payload: { error } })
  }, [])

  const startExecution = useCallback((workflowRef: WorkflowRefStrict, message?: string) => {
    dispatch({ type: 'EXECUTION_START', payload: { workflowRef, message } })
  }, [])

  const updateExecutionProgress = useCallback((progress: number, message?: string) => {
    dispatch({ type: 'EXECUTION_PROGRESS', payload: { progress, message } })
  }, [])

  const completeExecution = useCallback((executionResult: WorkflowExecutionResult) => {
    dispatch({ type: 'EXECUTION_COMPLETE', payload: { result: executionResult } })
  }, [])

  const errorExecution = useCallback((error: string) => {
    dispatch({ type: 'EXECUTION_ERROR', payload: { error } })
  }, [])

  const updateContext = useCallback(
    (
      context: Partial<
        Pick<UnifiedWorkflowState, 'workflowRef' | 'nodeId' | 'selectedNodeIds' | 'nodeExecutions'>
      >
    ) => {
      dispatch({ type: 'CONTEXT_UPDATE', payload: context })
    },
    []
  )

  const updateNodeExecution = useCallback((executionId: string, execution: NodeExecution) => {
    dispatch({ type: 'NODE_EXECUTION_UPDATE', payload: { executionId, execution } })
  }, [])

  const clearNodeExecutions = useCallback(() => {
    dispatch({ type: 'CONTEXT_UPDATE', payload: { nodeExecutions: {} } })
  }, [])

  // Computed properties (memoized for performance)
  const hasActiveWorkflowExecutions = useMemo(
    () =>
      Boolean(
        state.nodeExecutions &&
          Object.values(state.nodeExecutions).some((execution) =>
            ['pending', 'running', 'waiting_approval'].includes(execution.status)
          )
      ),
    [state.nodeExecutions]
  )

  const showThinkingAnimation = useMemo(
    () => state.isExecuting || state.isBuilding || hasActiveWorkflowExecutions,
    [state.isExecuting, state.isBuilding, hasActiveWorkflowExecutions]
  )

  const isIdle = useMemo(() => state.phase === 'idle', [state.phase])
  const isActive = useMemo(() => state.phase !== 'idle', [state.phase])
  const canExecute = useMemo(
    () => state.phase === 'idle' || state.phase === 'completed',
    [state.phase]
  )

  return {
    // State
    ...state,

    // Actions
    setPhase,
    resetState,
    startBuilding,
    updateBuildProgress,
    completeBuild,
    errorBuild,
    startExecution,
    updateExecutionProgress,
    completeExecution,
    errorExecution,
    updateContext,
    updateNodeExecution,
    clearNodeExecutions,

    // Computed properties
    hasActiveWorkflowExecutions,
    showThinkingAnimation,
    isIdle,
    isActive,
    canExecute,
  }
}

/**
 * Utility function to create workflow state manager with common options
 * Note: This should only be used within React components or custom hooks
 */
export const createWorkflowStateManager = (options?: WorkflowStateManagerOptions) => {
  // This is a factory function that returns a hook, not a hook itself
  return () => useWorkflowStateManager(options)
}

/**
 * Hook for components that only need to read workflow state
 */
export function useWorkflowState(manager: WorkflowStateManager) {
  return {
    phase: manager.phase,
    isExecuting: manager.isExecuting,
    isBuilding: manager.isBuilding,
    progress: manager.progress,
    hasActiveWorkflowExecutions: manager.hasActiveWorkflowExecutions,
    showThinkingAnimation: manager.showThinkingAnimation,
    executionResult: manager.executionResult,
    buildResult: manager.buildResult,
    error: manager.error,
    workflowRef: manager.workflowRef,
    nodeExecutions: manager.nodeExecutions,
    isIdle: manager.isIdle,
    isActive: manager.isActive,
    canExecute: manager.canExecute,
  }
}
